import {
  CodeableConcept,
  Encounter,
  Patient,
  Practitioner,
} from '@medplum/fhirtypes';
import { TFunction } from 'i18next';
import { getPatientOrPractitionerName } from '../../lims/utils/common-helpers';
import {
  SearchFieldDefinition,
  ResourceSearchConfig,
  FhirSearchParams,
} from '@/components/resource-search/ResourceSearchForm.types';
import { ResourceTableColumn } from '@/components/resource-search/ResourceTable';
import { BaseSearchParams } from '../../lims/hooks/useResourceSearch';
import { StatusBadge } from '@/components/medplum-react/StatusBadge/StatusBadge';
import { ARKON } from '@/consts/systems.consts';
import { useResource } from '@medplum/react';

export interface EncounterSearchParams extends BaseSearchParams {
  patientName?: string;
  status?: string;
  type?: string;
  typeDisplay?: string;
  participant?: string;
  participantResource?: Practitioner;
}

export const ENCOUNTER_SEARCH_DEFAULTS: EncounterSearchParams = {
  patientName: undefined,
  status: undefined,
  type: undefined,
  typeDisplay: undefined,
  participant: undefined,
  participantResource: undefined,
  dateFrom: null,
  dateTo: null,
};

export const encounterSearchConfig: ResourceSearchConfig<
  EncounterSearchParams,
  Encounter
> = {
  resourceType: 'Encounter',
  itemsPerPage: 20,
  buildSearchQuery: (params, page, itemsPerPage) => {
    const query: FhirSearchParams = {
      _count: itemsPerPage,
      _offset: (page - 1) * itemsPerPage,
      _sort: '-date',
    };

    if (params.status) {
      query.status = params.status;
    }

    if (params.type) {
      query.type = params.type;
    }

    if (params.participant) {
      query.participant = `Practitioner/${params.participant}`;
    }

    if (params.patientName) {
      query['subject:Patient.name'] = params.patientName;
    }

    if (params.dateFrom && params.dateTo) {
      const from = params.dateFrom.toISOString().split('T')[0];
      const to = params.dateTo.toISOString().split('T')[0];
      query._filter = `(date ge ${from} and date le ${to})`;
    } else if (params.dateFrom) {
      query.date = `ge${params.dateFrom.toISOString().split('T')[0]}`;
    } else if (params.dateTo) {
      query.date = `le${params.dateTo.toISOString().split('T')[0]}`;
    }

    return query;
  },
};

const ENCOUNTER_STATUS_OPTIONS = [
  { value: 'planned', translationKey: 'planned', defaultLabel: 'Planned' },
  {
    value: 'in-progress',
    translationKey: 'in-progress',
    defaultLabel: 'In Progress',
  },
  { value: 'finished', translationKey: 'finished', defaultLabel: 'Finished' },
  {
    value: 'cancelled',
    translationKey: 'cancelled',
    defaultLabel: 'Cancelled',
  },
] as const;

interface EncounterFieldOptions {
  t: TFunction<'encounters' | 'patient-pages'>;
  searchParams: EncounterSearchParams;
  onSearch: (updates: Partial<EncounterSearchParams>) => void;
  hidePatientName?: boolean;
  patientNameQuery: string;
  setPatientNameQuery: (value: string) => void;
  setPatientQuery: (value: string) => void;
  patientNameOptions: string[];
  patientLoading: boolean;
  patientNameLoading: boolean;
}

export function getEncounterSearchFields({
  t,
  searchParams,
  onSearch,
  hidePatientName,
  patientNameQuery,
  setPatientNameQuery,
  setPatientQuery,
  patientNameOptions,
  patientLoading,
  patientNameLoading,
}: EncounterFieldOptions): SearchFieldDefinition<EncounterSearchParams>[] {
  return [
    !hidePatientName && {
      key: 'patientName',
      type: 'autocomplete',
      placeholder: t('filters.patientName', {
        ns: 'encounters',
        defaultValue: 'Search by patient name...',
      }),
      value: patientNameQuery,
      options: patientNameOptions,
      loading: patientLoading || patientNameLoading,
      onQueryChange: (value: string) => {
        setPatientQuery(value);
        setPatientNameQuery(value);
        if (!value) {
          onSearch({ patientName: undefined });
        }
      },
      onOptionSubmit: (value: string) => onSearch({ patientName: value }),
      onEnter: (value: string) => onSearch({ patientName: value }),
    },
    {
      key: 'type',
      type: 'codeableConcept',
      placeholder: t('filters.typePlaceholder', {
        defaultValue: 'Select encounter type...',
      }),
      binding: ARKON.VS.ENCOUNTER_TYPE_EL,
      defaultValue: searchParams.type
        ? {
            coding: [
              {
                code: searchParams.type,
                display: searchParams.typeDisplay,
              },
            ],
          }
        : undefined,
      onChange: (value: CodeableConcept | undefined) => {
        const code = value?.coding?.[0]?.code;
        const display = value?.coding?.[0]?.display ?? value?.text;
        onSearch({
          type: code,
          typeDisplay: display,
        });
      },
    },
    {
      key: 'status',
      type: 'select',
      placeholder: t('filters.statusPlaceholder', {
        ns: 'encounters',
        defaultValue: 'Select status...',
      }),
      options: ENCOUNTER_STATUS_OPTIONS.map((option) => ({
        value: option.value,
        label: t(`statusOptions.${option.translationKey}`, {
          ns: 'encounters',
          defaultValue: option.defaultLabel,
        }),
      })),
      value: searchParams.status,
      onChange: (value: string | null) =>
        onSearch({ status: value || undefined }),
    },
    {
      key: 'participantResource',
      type: 'resource',
      resourceType: 'Practitioner',
      placeholder: t('filters.doctorPlaceholder', {
        ns: 'encounters',
        defaultValue: 'Select doctor...',
      }),
      value: searchParams.participantResource,
      onChange: (value: Practitioner | undefined) => {
        // Store both the full resource and the ID
        onSearch({
          participant: value?.id || undefined,
          participantResource: value || undefined,
        });
      },
    },
    {
      key: 'dateFrom',
      type: 'date',
      placeholder: t('filters.date.fromDate', {
        ns: 'encounters',
        defaultValue: 'From date',
      }),
      value: searchParams.dateFrom,
      onChange: (value: Date | null) => onSearch({ dateFrom: value }),
    },
    {
      key: 'dateTo',
      type: 'date',
      placeholder: t('filters.date.toDate', {
        ns: 'encounters',
        defaultValue: 'To date',
      }),
      value: searchParams.dateTo,
      onChange: (value: Date | null) => onSearch({ dateTo: value }),
    },
  ].filter(Boolean) as SearchFieldDefinition<EncounterSearchParams>[];
}

interface EncounterColumnOptions {
  t: TFunction<'encounters' | 'patient-pages'>;
  patients: Map<string, Patient>;
  hidePatientColumn?: boolean;
}

export function getEncounterTableColumns({
  t,
  hidePatientColumn,
}: EncounterColumnOptions): ResourceTableColumn<Encounter>[] {
  const columns: ResourceTableColumn<Encounter>[] = [
    {
      key: 'period',
      label: t('tableColumns.date', { ns: 'encounters', defaultValue: 'Date' }),
      render: (encounter) =>
        encounter.period?.start
          ? new Date(encounter.period.start).toLocaleDateString('en-GB')
          : '—',
    },
    {
      key: 'type',
      label: t('tableColumns.type', { ns: 'encounters', defaultValue: 'Type' }),
      render: (encounter) => {
        const code =
          encounter.type?.[0]?.coding?.[0]?.code ??
          encounter.type?.[0]?.text?.toLowerCase().replace(/\s+/g, '-');
        const textFromCoding =
          (code &&
            t(`types.${code}`, { ns: 'encounters', defaultValue: '' })) ||
          '';
        const fallbackText = encounter.type?.[0]?.text ?? '';
        return textFromCoding || fallbackText || code || '—';
      },
    },
    {
      key: 'status',
      label: t('tableColumns.status', {
        ns: 'encounters',
        defaultValue: 'Status',
      }),
      render: (encounter) => {
        const status = encounter.status ?? 'unknown';
        const label = t(`statusOptions.${status}`, {
          ns: 'encounters',
          defaultValue: status.replace(/-/g, ' '),
        });
        return <StatusBadge status={status} label={label} size="sm" />;
      },
    },
    {
      key: 'participant',
      label: t('tableColumns.participants', {
        ns: 'encounters',
        defaultValue: 'Participant',
      }),
      render: (encounter) => {
        const participants = encounter.participant ?? [];
        return (
          participants
            .map(
              (p) =>
                p.individual?.display ||
                p.individual?.reference ||
                t('detailsPage.unknown', {
                  ns: 'encounters',
                  defaultValue: 'Unknown',
                })
            )
            .join(', ') || '—'
        );
      },
    },
    {
      key: 'reason',
      label: t('tableColumns.reason', {
        ns: 'encounters',
        defaultValue: 'Reason',
      }),
      render: (encounter) => encounter.reasonCode?.[0]?.text || '—',
    },
  ];

  if (!hidePatientColumn) {
    columns.splice(0, 0, {
      key: 'patient',
      label: t('tableColumns.patient', {
        ns: 'encounters',
        defaultValue: 'Patient',
      }),
      render: (encounter) => {
        const patient = useResource<Patient>({
          reference: encounter.subject?.reference,
        });
        return patient
          ? getPatientOrPractitionerName(patient)
          : t('detailsPage.unknown', {
              ns: 'encounters',
              defaultValue: 'Unknown',
            });
      },
    });
  }

  return columns;
}
